import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { getBrokenLinkUrl } from "../../../common/utils";
import { isURL } from "../../../_editor/components/clipboard/clipboard.utils";
import { resolveBoxTiming } from "../../../common/resolveBoxTiming.es6";
import RecorderCanvasImage from "./RecorderCanvasImage";
import { sceneSeek, sceneTimeBus } from "../../../common/scene_clock.es6";
/* eslint-disable react/prop-types */

// Cache ratios by URL so re-mounts have it on the first render (no flash).
const ratioCache = new Map();

function checkHyperlink(hyperlink) {
  if (hyperlink === null || hyperlink === undefined) return false;
  hyperlink = hyperlink.replace(/\s/g, "");
  if (hyperlink === "") return false;
  if (hyperlink.substring(0, 4) === "www.") {
    hyperlink = "http://" + hyperlink;
  }
  return isURL(hyperlink) ? hyperlink : false;
}

export default function Image({ state: baseState, props, markElements }) {
  const brokenLinkUrl = getBrokenLinkUrl();
  // spike: click action — the click swaps in the after image and releases the pause gate.
  // The scene's oneClickEnded entry is the source of truth (reset on replay, restored on Back);
  // local state only covers images without a pause gate (no entry).
  const [localClicked, setLocalClicked] = useState(false);
  const gateEntry = props && props.oneClickEnded && props.oneClickEnded.find((e) => e.id === props.id);
  // After-click video: the gate stays held while it plays and is released on 'ended'.
  const [videoPlaying, setVideoPlaying] = useState(false);
  // The video never rides the timeline: while it plays the scene is held on demand, whatever pauseUntilClicked says.
  const holdRef = useRef(null);
  holdRef.current = props && props.holdForClickMedia;
  const heldRef = useRef(false);
  const setHold = (on) => {
    if (heldRef.current === on) return;
    heldRef.current = on;
    holdRef.current && holdRef.current(props.id, on);
  };
  useEffect(() => () => {
    if (heldRef.current) setHold(false);
  }, []);
  const clicked = gateEntry ? !!gateEntry.isClicked : localClicked;
  const clickAction = baseState.clickAction;
  const afterVideo = clickAction && clickAction.afterType === "video" ? clickAction.afterVideo : "";
  const clickable =
    !!clickAction && !(props && props.recorderMode === true) && !clicked && !videoPlaying;
  const state =
    clicked && clickAction && !afterVideo && clickAction.afterImage
      ? { ...baseState, url: clickAction.afterImage }
      : baseState;
  const onActionClick = clickable
    ? (e) => {
        if (e && e.target && e.target.closest && e.target.closest("[data-pin]")) return;
        if (afterVideo) {
          setHold(true);
          setVideoPlaying(true);
          if (!clickAction.pauseUntilClicked) setLocalClicked(true);
          return;
        }
        setLocalClicked(true);
        props.setOneClickEnded && props.setOneClickEnded(props.id);
      }
    : undefined;
  const wrapStyle = { height: "100%", width: "100%", ...(clickable ? { cursor: "pointer" } : {}) };
  const [hasImageError, setHasImageError] = useState(false);
  // Safari sizes a max-content box from the image's natural px (ignoring height),
  // overflowing it; for those boxes derive width from height via aspect-ratio.
  const [naturalRatio, setNaturalRatio] = useState(() => ratioCache.get(state.url) || null);
  const [autoWidthBox, setAutoWidthBox] = useState(false);
  // cache-busting: sadece WebP + replayWebp aktifken sahne değişiminde animasyonu sıfırlar
  const [cacheBuster, setCacheBuster] = useState(0);
  // Frame-accurate canvas decode (RecorderCanvasImage) failed — CORS, missing
  // ImageDecoder, or timeout. Fall back to the normal <img> path below instead
  // of retrying every render.
  const [webpDecodeFailed, setWebpDecodeFailed] = useState(false);

  const draggableImageVisorRef = useRef(null);
  const imgRef = useRef(null);

  // WebP replay blob URL'ini sakla — her render'da yeni URL üretme
  const webpBlobUrlRef = useRef(null);
  const lastCacheBusterRef = useRef(0);

  const currentView = props && props.currentView;
  const currentShow = props && props.show;
  const isWebp =
    state.url && typeof state.url === "string" && state.url.toLowerCase().includes(".webp");
  const shouldReplayWebp = isWebp && state.replayWebp !== false;

  // currentView veya show değiştiğinde cacheBuster'ı güncelle → sadece WebP replay için
  const prevViewRef = useRef(null);
  const prevShowRef = useRef(null);
  // Timeline başa sarılıp (loop/rewind) tekrar oynatıldığında: kutu sahne
  // başından beri görünürse (startFrame <= loop noktası) `show` hiç false'a
  // düşmeden sürekli true kalır — bu yüzden showBecameTrue asla tetiklenmez
  // ve WebP bir daha animasyonlu oynamaz (tek seferlik oynayıp son karede
  // kalır). Playhead'in belirgin şekilde geri gitmesini de replay
  // tetikleyicisi say.
  const prevFrameRef = useRef(null);
  const recorder = props && props.recorderMode === true;
  // The recorder steps the frame prop; live playback reads the committed frame.
  const currentFrameTime = recorder ? props.visorFrameTime : undefined;
  const replayWebp = () => {
    setCacheBuster(Date.now());
    setHasImageError(false);
  };
  const rewindRef = useRef(null);
  rewindRef.current = () => {
    if (shouldReplayWebp && currentShow) replayWebp();
  };

  useEffect(() => {
    const viewChanged = JSON.stringify(prevViewRef.current) !== JSON.stringify(currentView);
    const showBecameTrue = !prevShowRef.current && currentShow;
    const rewound =
      typeof currentFrameTime === "number" &&
      typeof prevFrameRef.current === "number" &&
      currentFrameTime < prevFrameRef.current - 1;

    if ((viewChanged || showBecameTrue || (rewound && currentShow)) && shouldReplayWebp) {
      replayWebp();
    } else if (viewChanged || showBecameTrue) {
      setHasImageError(false);
    }

    prevViewRef.current = currentView;
    prevShowRef.current = currentShow;
    prevFrameRef.current = currentFrameTime;
  }, [currentView, currentShow, shouldReplayWebp, currentFrameTime]);

  // Live: the frame loop only moves forward, so a committed frame behind the last one
  // is an explicit move back (a seek or a replay; a replay has no seek id).
  const ownScene = Array.isArray(currentView) ? currentView[currentView.length - 1] : currentView;
  useEffect(() => {
    if (recorder || ownScene == null) return undefined;
    let prev = sceneTimeBus.frameSceneId === ownScene ? sceneTimeBus.frame : null;
    return sceneTimeBus.subscribeFrame(() => {
      if (sceneTimeBus.frameSceneId !== ownScene) return;
      const f = sceneTimeBus.frame;
      if (prev != null && f < prev - 1) rewindRef.current();
      prev = f;
    });
  }, [recorder, ownScene]);

  // A new url (e.g. re-animated image) deserves a fresh decode attempt.
  useEffect(() => {
    setWebpDecodeFailed(false);
  }, [state.url]);

  // Bileşen silindiğinde (unmount) RAM'deki WebP Blob URL'sini temizle
  useEffect(() => {
    return () => {
      if (webpBlobUrlRef.current) {
        URL.revokeObjectURL(webpBlobUrlRef.current);
      }
    };
  }, []);

  const getImageSrc = useCallback(
    (url) => {
      if (!url || hasImageError) return brokenLinkUrl;

      // WebP + replayWebp aktif → blob'dan URL oluştur
      if (shouldReplayWebp && cacheBuster > 0) {
        if (window.__visorImageBlobData && window.__visorImageBlobData[url]) {
          // Sadece cacheBuster değiştiğinde yeni blob URL üret (replay tetiklendi)
          if (cacheBuster !== lastCacheBusterRef.current) {
            // Eski blob URL'i serbest bırak
            if (webpBlobUrlRef.current) {
              URL.revokeObjectURL(webpBlobUrlRef.current);
            }
            webpBlobUrlRef.current = URL.createObjectURL(window.__visorImageBlobData[url]);
            lastCacheBusterRef.current = cacheBuster;
          }
          return webpBlobUrlRef.current;
        }
        // Henüz blob cache'te değil — cache-busting ile server'dan
        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}_t=${cacheBuster}`;
      }

      // Non-WebP veya replay kapalı: doğrudan orijinal URL (browser disk cache'ten yükler)
      return url;
    },
    [brokenLinkUrl, hasImageError, cacheBuster, shouldReplayWebp]
  );

  const hyperlink = checkHyperlink(state.hyperlink);
  const scale = state.scale || 1;
  const translateX = (state.translate ? state.translate.x : 0) || 0;
  const translateY = (state.translate ? state.translate.y : 0) || 0;
  const transform = `translate(${translateX}%,${translateY}%) scale(${scale})`;
  const imageHeight = state.allowDeformed ? "" : "auto";
  const imageSrc = hasImageError ? brokenLinkUrl : getImageSrc(state.url);

  // Measure intrinsic ratio + detect a max-content box before paint (no flash).
  // SVGs use a detached probe — Safari's in-DOM naturalWidth for them is wrong.
  useLayoutEffect(() => {
    const img = imgRef.current;
    const box = img && img.closest(".wholebox, .wholeboxvisor");
    setAutoWidthBox(!!box && box.style.width === "max-content");

    const applyRatio = (w, h) => {
      if (!w || !h) return;
      const r = w / h;
      ratioCache.set(state.url, r);
      setNaturalRatio(r);
    };

    const isSvg = /\.svg(\?|#|$)/i.test(state.url || "");
    if (!isSvg && img && img.complete && img.naturalWidth && img.naturalHeight) {
      applyRatio(img.naturalWidth, img.naturalHeight);
      return undefined;
    }

    if (!imageSrc) return undefined;
    // Already measured this file — probing again is a second download.
    if (ratioCache.has(state.url)) return undefined;
    // window.Image: this component is named Image, shadowing the global ctor.
    const probe = new window.Image();
    const read = () => applyRatio(probe.naturalWidth, probe.naturalHeight);
    probe.onload = read;
    // Preloader's bytes when resident, so measuring costs no transfer.
    probe.src =
      (typeof window.__visorImageObjectUrl === "function" &&
        window.__visorImageObjectUrl(imageSrc)) ||
      imageSrc;
    if (probe.complete) read();
    return () => {
      probe.onload = null;
    };
  }, [imageSrc, state.url]);

  // Animated images (WebP / GIF / animated PNG / AVIF) play on wall-clock in
  // a plain <img> — decoupled from the timeline/playhead. Decode via
  // WebCodecs ImageDecoder instead and pick the right frame for
  // visorFrameTime, so scrub/seek/rewind always show the frame that matches
  // the current playback position. Recorder mode additionally needs this
  // because its seek-frame capture loop runs much faster than wall-clock, so
  // an 8s WebP would otherwise finish in ~1s of captured video and stall.
  // Static formats decode to a single frame and are painted once — see
  // RecorderCanvasImage's own header comment.
  if (afterVideo && (clicked || videoPlaying)) {
    const finishVideo = () => {
      setHold(false);
      setVideoPlaying(false);
      setLocalClicked(true);
      props.setOneClickEnded && props.setOneClickEnded(props.id);
    };
    return (
      <div style={wrapStyle} className="draggableImageVisor">
        <video
          src={afterVideo}
          autoPlay={videoPlaying}
          playsInline
          preload="auto"
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
          onError={finishVideo}
          onEnded={finishVideo}
        />
      </div>
    );
  }

  const isAnimatedFormat =
    typeof state.url === "string" && /(\.webp|\.gif|\.apng|\.avif)(\?|#|$)/i.test(state.url);
  if (isAnimatedFormat && !hasImageError && !webpDecodeFailed) {
    // Box mount frame is what aligns the animation's t=0 to "now". Use the
    // canonical resolveBoxTiming so this matches AnimatedWrapper's
    // recorderTimeSec calc (both keyed off the same startFrame).
    const parentBox = props && props.parentBox;
    const boxId = props && props.id;
    const legacyAnims =
      (boxId &&
        window.IdealStudio &&
        window.IdealStudio.State &&
        window.IdealStudio.State.animationsById &&
        window.IdealStudio.State.animationsById[boxId]) ||
      [];
    const resolved = parentBox ? resolveBoxTiming(parentBox, legacyAnims) : {};
    const boxStartFrame = typeof resolved.startFrame === "number" ? resolved.startFrame : 0;
    // ImageDecoder needs the original WebP/GIF bytes — bypass getImageSrc()
    // which produces a blob URL (re-encoded blob can lose animation chunks
    // when fetched through CORS). Pass state.url so the decoder gets the
    // server's bytes verbatim.
    const recCanvas = (
      <RecorderCanvasImage
        url={state.url}
        visorFrameTime={props.visorFrameTime}
        boxStartFrame={boxStartFrame}
        fps={30}
        show={props.show !== false}
        playing={(props.sceneAdvancing ?? props.visorPlaying) !== false}
        seekId={sceneSeek.id}
        frameSceneId={
          Array.isArray(currentView) ? currentView[currentView.length - 1] : currentView
        }
        onReady={props.onVideoCanPlayThrough && (() => props.onVideoCanPlayThrough(boxId))}
        onDecodeFailed={() => setWebpDecodeFailed(true)}
        imgStyle={{
          height: imageHeight,
          transform,
          WebkitTransform: transform,
          MozTransform: transform,
        }}
      />
    );
    return (
      <div
        style={wrapStyle}
        onClick={onActionClick}
        className="draggableImageVisor"
        ref={draggableImageVisorRef}
      >
        {hyperlink ? (
          <a href={hyperlink} target="_blank" style={{ height: "100%", display: "block" }}>
            {recCanvas}
            {markElements}
          </a>
        ) : (
          <>
            {recCanvas}
            {markElements}
          </>
        )}
      </div>
    );
  }

  const imageElement = (
    <img
      ref={imgRef}
      className="basicImageClass"
      style={{
        overflow: "hidden",
        width: autoWidthBox ? "auto" : "100%",
        height: autoWidthBox ? "100%" : imageHeight,
        aspectRatio: autoWidthBox && naturalRatio ? naturalRatio : undefined,
        transform,
        WebkitTransform: transform,
        MozTransform: transform,
      }}
      src={imageSrc}
      onError={(e) => {
        e.target.onerror = null;
        setHasImageError(true);
      }}
    />
  );

  return (
    <div
      style={wrapStyle}
      onClick={onActionClick}
      className="draggableImageVisor"
      ref={draggableImageVisorRef}
    >
      {hyperlink ? (
        <a href={hyperlink} target="_blank" style={{ height: "100%", display: "block" }}>
          {imageElement}
          {markElements}
        </a>
      ) : (
        <>
          {imageElement}
          {markElements}
        </>
      )}
    </div>
  );
}
/* eslint-enable react/prop-types */
